/**
 * enemySpawnParser.ts
 * Parses the Entities object layer of a Tiled map into `EnemySpawnData[]`.
 *
 * Extracted from {@link ./TileMapLoader} to keep that file under the
 * AGENTS.md 300-line cap. The dispatch table here is the single source of
 * truth for valid `enemy_*` Tiled object type strings — any new enemy
 * type must be added to both the union member here and the corresponding
 * `case` in `src/game/scenes/playSceneSpawns.ts`.
 *
 * No rendering or game-state imports — pure data transform.
 *
 * @module enemySpawnParser
 */

import type { TiledObjectGroup } from '../rendering/TileMapTypes';
import Logger from '../utils/logger';

/**
 * Entities-layer object types that are deliberately not enemies.
 *
 * `player_spawn` is consumed by {@link ./TileMapLoader}; `exit` is an authoring
 * marker for the level's goal position and is not read by the runtime, which
 * derives level completion from the right edge instead. Listing them here
 * keeps the unknown-type warning below signal rather than noise.
 */
const NON_ENEMY_OBJECT_TYPES: ReadonlySet<string> = new Set(['player_spawn', 'exit']);

/** Data for a single enemy spawn point parsed from the Entities object layer. */
export interface EnemySpawnData {
  type:
    | 'nibbler'
    | 'thornhop'
    | 'gloom_bat'
    // DECISION: W3 greybox enemy types. Each maps to an entity class under
    // src/game/entities/enemies/ — see PlayScene._spawnEnemies for the
    // type → constructor dispatch.
    | 'shellback'
    | 'spark_imp'
    | 'sentinel_bloom'
    | 'gearling'
    // World 3 miniboss (Brasscloud Foundry). Ground-walking armoured
    // charger. (x, y) is the Tiled top-left of the 36×32 hitbox.
    | 'bramble_ram'
    // World 4 miniboss (Brasscloud Foundry). Ground-walking hammer+shield
    // mech that advances on Nova and is only vulnerable during its steam
    // vent window. (x, y) is the Tiled top-left of the 40×36 hitbox.
    | 'forge_warden'
    // World 5 enemy (Eclipse Bastion). Ghost that replays Nova's position
    // from 2 s ago — no gravity and no platform collisions, so (x, y) is a
    // free-floating spawn point and needs no floor beneath it.
    | 'shadow_glider'
    // World 5 miniboss (Eclipse Bastion). Ground-walking crystal clone that
    // mirrors Nova horizontally across a fixed arena axis. (x, y) is the
    // Tiled top-left of the 22×30 hitbox. Set the Tiled object's `width` to
    // the SPAN of the mirror hall; the loader stores `mirrorAxisX` as the
    // object's horizontal centre, which is the line the clone reflects across.
    | 'tide_mirror'
    // World 3 boss (Brasscloud Foundry). Lives in 3-B; (x, y) is the
    // Tiled top-left of the boss hitbox, same convention as every other
    // enemy in this union. To centre a boss visually at (cx, cy), author
    // the Tiled object at (cx - W/2, cy - H/2). KingRivet is aerial and
    // has no gravity, so y is NOT a ground-row spawn.
    | 'king_rivet'
    // World 4 boss (Crystal Tide Ruins). Lives in 4-B; aerial, autonomous,
    // serpentine eel. (x, y) is the Tiled top-left of the boss hitbox,
    // same convention as every other enemy in this union — the boss
    // derives its internal patrol/bob centre as (x + W/2, y + H/2).
    // Set the Tiled object's `width` to the patrol DIAMETER you want;
    // the loader stores `patrolHalfWidth` as `width / 2`.
    | 'prism_leviathan'
    // World 5 boss (Eclipse Bastion). Lives in 5-B; aerial, autonomous,
    // stationary on X and bobbing on Y. (x, y) is the Tiled top-left of
    // the 80 × 80 boss hitbox, same convention as every other enemy in
    // this union. Takes no `width` metadata — the titan does not patrol.
    | 'star_eater_boss';
  x: number;
  y: number;
  /**
   * Patrol half-width for {@link PrismLeviathan}. Populated by the
   * loader from the Tiled object's `width` (half of it). Ignored by
   * every other enemy type. Optional so we don't have to touch every
   * other spawn record — the dispatcher falls back to a default.
   */
  patrolHalfWidth?: number;
  /**
   * Mirror-reflection axis for {@link TideMirror}, in world pixels.
   * Populated by the loader from the Tiled object's horizontal centre
   * (`x + width / 2`), so authoring the object to span the mirror hall puts
   * the axis dead centre. Ignored by every other enemy type. Optional so we
   * don't have to touch every other spawn record — the dispatcher falls back
   * to the clone's own spawn x, which is the entity's constructor default.
   */
  mirrorAxisX?: number;
}

/**
 * Parse the Entities object layer into an array of enemy spawn definitions.
 *
 * Object types listed in {@link NON_ENEMY_OBJECT_TYPES} are skipped quietly —
 * they belong to other consumers. Anything else is skipped *and logged*: an
 * unrecognised type means a level authored something the game will not spawn,
 * and dropping it without a word is the failure mode that left 222 spike tiles
 * inert across 13 levels.
 *
 * @param entitiesLayer The `Entities` object group, or `undefined` if the
 *                      Tiled map has no such layer (returns `[]`).
 * @returns Enemy spawn definitions in Tiled-document order.
 */
export function parseEnemySpawns(
  entitiesLayer: TiledObjectGroup | undefined,
): EnemySpawnData[] {
  if (!entitiesLayer) return [];

  const spawns: EnemySpawnData[] = [];
  // Parse enemy spawn objects. W1 types: enemy_nibbler / enemy_thornhop /
  // enemy_gloom_bat. W3 types: enemy_shellback / enemy_spark_imp /
  // enemy_sentinel_bloom / enemy_gearling. W5 type: enemy_shadow_glider.
  // Minibosses: enemy_bramble_ram (W3) / enemy_forge_warden (W4) /
  // enemy_tide_mirror (W5). Bosses: enemy_king_rivet (W3) /
  // enemy_prism_leviathan (W4) / enemy_star_eater_boss (W5).
  for (const obj of entitiesLayer.objects) {
    if (obj.type === 'enemy_nibbler') {
      spawns.push({ type: 'nibbler', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_thornhop') {
      spawns.push({ type: 'thornhop', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_gloom_bat') {
      spawns.push({ type: 'gloom_bat', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_shellback') {
      spawns.push({ type: 'shellback', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_spark_imp') {
      spawns.push({ type: 'spark_imp', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_sentinel_bloom') {
      spawns.push({ type: 'sentinel_bloom', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_gearling') {
      spawns.push({ type: 'gearling', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_bramble_ram') {
      spawns.push({ type: 'bramble_ram', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_forge_warden') {
      spawns.push({ type: 'forge_warden', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_shadow_glider') {
      spawns.push({ type: 'shadow_glider', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_tide_mirror') {
      // DECISION: Encode the mirror hall's span via the Tiled object's
      // `width` — the same free metadata channel `prism_leviathan` uses for
      // patrol diameter — and reflect across the object's CENTRE. Authoring
      // the object across the hall therefore puts the axis exactly halfway,
      // which is the only placement that maps the hall onto itself (any
      // off-centre axis reflects part of the hall outside it). Guard `> 0`
      // in case a Tiled author forgot to size the object; the dispatcher
      // then falls back to the clone's spawn x.
      const axisX = obj.width > 0 ? obj.x + obj.width / 2 : undefined;
      spawns.push({
        type: 'tide_mirror',
        x: obj.x,
        y: obj.y,
        ...(axisX !== undefined ? { mirrorAxisX: axisX } : {}),
      });
    } else if (obj.type === 'enemy_king_rivet') {
      spawns.push({ type: 'king_rivet', x: obj.x, y: obj.y });
    } else if (obj.type === 'enemy_prism_leviathan') {
      // DECISION: Encode patrol diameter via the Tiled object's `width`
      // (free metadata channel, ignored by every other enemy parse
      // case). `obj.width` is guaranteed by the TiledObject contract;
      // guard `> 0` in case a Tiled author forgot to size the object.
      const halfWidth = obj.width > 0 ? obj.width / 2 : undefined;
      spawns.push({
        type: 'prism_leviathan',
        x: obj.x,
        y: obj.y,
        ...(halfWidth !== undefined ? { patrolHalfWidth: halfWidth } : {}),
      });
    } else if (obj.type === 'enemy_star_eater_boss') {
      spawns.push({ type: 'star_eater_boss', x: obj.x, y: obj.y });
    } else if (!NON_ENEMY_OBJECT_TYPES.has(obj.type)) {
      Logger.warn(
        'LevelLoader',
        `Unrecognised Entities object type "${obj.type}" at (${obj.x}, ${obj.y}) — skipped.`,
      );
    }
  }
  return spawns;
}
